import { ThemeColors } from "@/constants";
import ClipArea from "@/models/ClipArea";
import { fabric } from "fabric";
import Hammer from "hammerjs";
import { parse } from "svg-parser";

export function init(canvas: any, hammer: any, listener: any) {
  const pan = new Hammer.Pan();
  const rotate = new Hammer.Rotate();
  const pinch = new Hammer.Pinch();
  const press = new Hammer.Press();

  fabric.Object.prototype.transparentCorners = false;
  fabric.Object.NUM_FRACTION_DIGITS = 15;
  fabric.Object.prototype.cornerColor = ThemeColors.Primary;
  fabric.Object.prototype.cornerStyle = "circle";
  fabric.Object.prototype.borderColor = ThemeColors.Transparent;

  document
    .getElementsByClassName("canvas-container")[0]
    ?.classList?.add("m-auto");
  //   const tap = new Hammer.Tap({touchAction: 'manipulation'});
  const tap = new Hammer.Tap();

  const contextLines = canvas.getContext("2d");
  canvas.backgroundColor = ThemeColors.Darker;

  const drawSessionList: any = [];
  canvas.freeDrawingBrush.color = "black";

  centerObjects(canvas);

  let lastX: any = null;
  let lastY: any = null;

  let adjustDeltaX = 0;
  let adjustDeltaY = 0;
  let adjustScale = 1;
  let adjustScaleX = 1;
  let adjustScaleY = 1;
  let adjustRotation = 0;
  let lastEvent: any = null;

  const currentDeltaX: any = null;
  const currentDeltaY: any = null;
  let currentScale: any = null;
  let currentScaleX: any = null;
  let currentScaleY: any = null;
  const currentRotation: any = null;
  const rotateSnaps = [0, 45, 90, 135, 180, 225, 270, 315, 360];

  hammer.add([pan, pinch, rotate, tap, press]);
  hammer.get("pinch").set({ enable: true });
  hammer.get("rotate").set({ enable: true });
  hammer.get("pan").set({ enable: true });
  hammer.get("press").set({ enable: true });
  pinch.recognizeWith(pan);
  pan.recognizeWith(pinch);

  hammer.on("panstart pinchstart rotatestart", (e: any) => {
    const activeObject = canvas.getActiveObject();
    emitEvent(canvas, true, true, listener, e);

    if (e.type === "pinchstart" || e.type === "rotatestart") {
      adjustRotation -= e.rotation - (activeObject?.angle || 0);
    } else {
      adjustRotation = -e.rotation;
    }

    lastX = e.center.x;
    lastY = e.center.y;

    if (activeObject) {
      adjustScaleX = activeObject.scaleX;
      adjustScaleY = activeObject.scaleY;
    }
    lastEvent = e;

    activeObject?.set({
      lockRotation: false,
      lockMovementX: false,
      lockMovementY: false,
    });
  });

  hammer.on("panmove", (e: any) => {
    emitEvent(canvas, false, true, listener, e);
    lastEvent = e;
  });

  document.onkeydown = (e: any) => {
    const object = canvas?.getActiveObject();
    if (object) {
      switch (e.keyCode) {
        case 46:
        case 8: // delete
          canvas.remove(object);
      }
      canvas.discardActiveObject();
      canvas.renderAll();
    }
  };

  canvas.on("object:rotating", (e: any) => {
    if (rotateSnaps.includes(Math.abs(Math.ceil(e.target.angle)))) {
      e.target.lockedDegree = Math.ceil(e.target.angle);
    } else {
      e.target.lockedDegree = null;
    }

    adjustRotation = e.target.angle;

    drawRotateGuidelines(
      canvas,
      canvas.getActiveObject(),
      contextLines,
      rotateSnaps
    );
    emitEvent(canvas, true, false, listener, e.e);
  });

  hammer.on("pinchmove rotatemove", (e: any) => {
    const activeObject = canvas.getActiveObject();
    emitEvent(canvas, true, true, listener, e);

    if (activeObject && e.maxPointers == 2) {
      const currentRotation = checkRotateSnap(
        canvas,
        e.rotation,
        activeObject,
        adjustRotation,
        contextLines,
        rotateSnaps
      );

      if (currentRotation != null) {
        activeObject.rotate(currentRotation);
      }

      // -----------------------------------
      // SCALING
      // -----------------------------------

      currentScale = adjustScale * e.scale;
      currentScaleX = adjustScaleX * e.scale;
      currentScaleY = adjustScaleY * e.scale;

      // Blocks object from being resized too small (and maintains aspect ratio)
      if (
        currentScaleX > activeObject.minScaleLimit &&
        currentScaleY > activeObject.minScaleLimit
      ) {
        console.warn("Not resizing: minScaleLimit reached 😮");
      } else {
        currentScaleX = activeObject.scaleX;
        currentScaleY = activeObject.scaleY;
      }

      const deltaScaleX = currentScaleX;
      const deltaScaleY = currentScaleY;

      // Temporarily locking regular object dragging during the gesture, just to smooth out jitteryness
      activeObject.set({ lockMovementX: true, lockMovementY: true });

      activeObject.set("scaleX", deltaScaleX);
      activeObject.set("scaleY", deltaScaleY);

      activeObject.setCoords();

      canvas.renderAll();

      if (activeObject.lockRotation == true) {
        drawRotateGuidelines(canvas, activeObject, contextLines, rotateSnaps);
      }

      activeObject.setCoords();

      canvas.renderAll();
    }
    lastEvent = e;
  });

  hammer.on("panend pinchend rotateend", (e: any) => {
    emitEvent(canvas, false, false, listener, e);
    const activeObject = canvas.getActiveObject();
    contextLines.clearRect(0, 0, canvas.width, canvas.height);

    if (activeObject) {
      adjustScale = currentScale;
      adjustRotation = currentRotation;
      adjustDeltaX = currentDeltaX;
      adjustDeltaY = currentDeltaY;
      adjustScaleX = currentScaleX;
      adjustScaleY = currentScaleY;

      // Timeout is to smooth out jitteryness, especially on iOS (iPad)
      // setTimeout(function () {
      //   activeObject.set({
      //     lockRotation: false,
      //     lockMovementX: false,
      //     lockMovementY: false,
      //   });
      // }, 2000);
    }

    canvas.renderAll();
    lastEvent = e;
    if (e.type === "pinchend" || e.type === "rotateend") {
      canvas.discardActiveObject().renderAll();
    }
  });

  canvas.on({
    "object:added": (event: any) => {
      const target = event.target;
      if (event.target.type != "path") {
        event.target.set({
          snapAngle: 45,
          snapThreshold: 10,
          originX: "center",
          originY: "center",
        });
      }

      if (event.target.type === "group") {
        target.set({
          snapAngle: 45,
          snapThreshold: 10,
          top: target.top + target.height / 2 + target.strokeWidth / 2,
          left: target.left + target.width / 2 + target.strokeWidth / 2,
          originX: "center",
          originY: "center",
        });
      }

      if (canvas.isDrawingMode == 1 && event.target.stroke != undefined) {
        drawSessionList.push(event.target);
      }
    },
  });
  return canvas;
}

export function resizeCanvas(canvas: any) {
  centerObjects(canvas);
  canvas.setWidth(canvas.width);
  canvas.setHeight(canvas.height);
  canvas.renderAll();
}

export function between(value: any, first: any, last: any) {
  const lower = Math.min(first, last);
  const upper = Math.max(first, last);
  return value >= lower && value <= upper;
}

function download(url: string, name: string) {
  // make the link. set the href and download. emulate dom click
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  // window.location.href = url;
}
export function downloadCustomization(canvas: any, name: string) {
  download(canvas.toDataURL("image/jpeg", 1.0), name + ".png");
}

export function convertBase64ToBlob(base64Image: string) {
  const parts = base64Image.split(";base64,");
  const imageType = parts[0].split(":")[1];
  const decodedData = window.atob(parts[1]);
  const uInt8Array = new Uint8Array(decodedData.length);
  for (let i = 0; i < decodedData.length; ++i) {
    uInt8Array[i] = decodedData.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: imageType });
}

export function centerObjects(canvas: any) {
  const objects = canvas.getObjects();
  const selection = new fabric.ActiveSelection(objects, {
    canvas,
  });
  selection.center();
  selection.destroy();
}

export function drawRotateGuidelines(
  canvas: any,
  object: any,
  contextLines: any,
  rotateSnaps: any
) {
  contextLines.clearRect(0, 0, canvas.width, canvas.height);

  if (rotateSnaps.includes(Math.abs(Math.ceil(object.angle)))) {
    const scale = object.scaleX;
    let XYstart = fabric.util.transformPoint(
      new fabric.Point(object.left - (object.width * scale) / 2, object.top),
      canvas.viewportTransform
    );
    const cp = object.getCenterPoint();
    const XYmid = fabric.util.transformPoint(
      new fabric.Point(cp.x, cp.y),
      canvas.viewportTransform
    );
    let XYend = fabric.util.transformPoint(
      new fabric.Point(object.left + (object.width * scale) / 2, object.top),
      canvas.viewportTransform
    );

    contextLines.save();

    let middlePoint = { x: XYmid.x, y: XYmid.y };
    contextLines.translate(middlePoint.x, middlePoint.y);
    contextLines.rotate((Math.PI / 180) * object.angle);
    contextLines.translate(-middlePoint.x, -middlePoint.y);
    contextLines.strokeStyle = "#3988ad";
    contextLines.lineWidth = 1;
    contextLines.beginPath();
    contextLines.moveTo(XYstart.x, XYstart.y);

    contextLines.lineTo(XYend.x, XYend.y);

    contextLines.stroke();
    contextLines.restore();

    XYstart = fabric.util.transformPoint(
      new fabric.Point(object.left - (object.height * scale) / 2, object.top),
      canvas.viewportTransform
    );
    XYend = fabric.util.transformPoint(
      new fabric.Point(object.left + (object.height * scale) / 2, object.top),
      canvas.viewportTransform
    );
    contextLines.save();
    middlePoint = { x: XYmid.x, y: XYmid.y };
    contextLines.translate(middlePoint.x, middlePoint.y);
    contextLines.rotate((Math.PI / 180) * (object.angle + 90));
    contextLines.translate(-middlePoint.x, -middlePoint.y);
    contextLines.strokeStyle = "#3988ad";
    contextLines.lineWidth = 1;
    contextLines.beginPath();
    contextLines.moveTo(XYstart.x, XYstart.y);
    contextLines.lineTo(XYend.x, XYend.y);
    contextLines.stroke();
    contextLines.restore();
    canvas.renderAll();
  }
}

export function checkRotateSnap(
  canvas: any,
  degree: any,
  object: any,
  adjustRotation: number,
  contextLines: any,
  rotateSnaps: any
) {
  const inDegree = degree + adjustRotation;
  let newDegree = null;

  if (object.lockRotation == true) {
    if (
      between(
        Math.abs(Math.ceil(inDegree)),
        Math.abs(object.lockedDegree) - 10,
        Math.abs(object.lockedDegree) + 10
      )
    ) {
      object.lockRotation = true;
      newDegree = object.lockedDegree;
    } else {
      object.lockRotation = false;
      object.lockedDegree = null;
      newDegree = degree + adjustRotation;
      contextLines.clearRect(0, 0, canvas.width, canvas.height);
    }
  } else {
    if (rotateSnaps.includes(Math.abs(Math.ceil(inDegree)))) {
      object.lockRotation = true;

      newDegree = Math.ceil(degree + adjustRotation);

      object.lockedDegree = newDegree;
    } else {
      object.lockRotation = false;
      object.lockedDegree = null;
      newDegree = degree + adjustRotation;
      contextLines.clearRect(0, 0, canvas.width, canvas.height);
    }
  }
  return newDegree;
}

export function belongsToRect(point: any, rect: any, radius = 0) {
  return (
    point.x + radius >= rect.left &&
    point.x <= rect.left + rect.width - radius &&
    point.y + radius >= rect.top &&
    point.y <= rect.top + rect.height - radius
  );
}

export function scaleInputJson(value: any, scale: number) {
  value?.objects?.forEach((element: any) => {
    element.scaleX = element.scaleX * scale;
    element.scaleY = element.scaleY * scale;
    element.top = element.top * scale;
    element.left = element.left * scale;
  });
  return value;
}

export function scaleOutputJson(value: any, scale: number) {
  value?.objects?.forEach((element: any) => {
    element.scaleX = element.scaleX / scale;
    element.scaleY = element.scaleY / scale;
    element.top = element.top / scale;
    element.left = element.left / scale;
  });
  return value;
}

export function scaleOutputObject(object: any, scale: number) {
  object.scaleX = object.scaleX / scale;
  object.scaleY = object.scaleY / scale;
  object.top = object.top / scale;
  object.left = object.left / scale;
  return object;
}

export function loadImage(path: string) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = path;
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (e) => {
      reject(e);
    };
  });
}

export function overlappingRects(rect1: ClipArea, rect2: ClipArea) {
  const [left1, top1, right1, bottom1] = [
    rect1.x,
    rect1.y,
    rect1.x + rect1.width,
    rect1.y + rect1.height,
  ];

  const [left2, top2, right2, bottom2] = [
    rect2.x,
    rect2.y,
    rect2.x + rect2.width,
    rect2.y + rect2.height,
  ];

  if (top1 < bottom2 || top2 < bottom1) {
    return false;
  }

  if (right1 < left2 || right2 < left1) {
    return false;
  }
  return true;
}

function emitEvent(
  canvas: any,
  pinching: boolean,
  drag: boolean,
  listener: any,
  event: any
) {
  const activeObject = canvas.getActiveObject();
  if (activeObject) {
    const { x, y } = activeObject.getCenterPoint();

    listener.onDrag({
      object: activeObject,
      value: drag,
      objectCenter: { x: x + canvas._offset.left, y: y + canvas._offset.top },
      eventCenter: {
        x: event?.center?.x || event.x,
        y: event?.center?.y || event.y,
      },
    });

    listener.onPinch({
      object: activeObject,
      value: pinching,
      objectCenter: { x: x + canvas._offset.left, y: y + canvas._offset.top },
      eventCenter: {
        x: event?.center?.x || event.x,
        y: event?.center?.y || event.y,
      },
    });
  }
}
